import APIService from "./APIService";

/**
 * Service for handling email report generator API operations
 */
const EmailReportGeneratorAPI = {
  /**
   * Generate email reports for a specific user within a time range
   * @param {string} userUuid - The user UUID
   * @param {string} startDate - Start date in ISO date-time format (YYYY-MM-DDTHH:mm:ss)
   * @param {string} endDate - End date in ISO date-time format (YYYY-MM-DDTHH:mm:ss)
   * @returns {Promise} - Promise that resolves with the API response containing generated reports
   */
  generateReportsForUser: async (userUuid, startDate, endDate) => {
    console.log(
      `[EmailReportGeneratorAPI] Generating reports for user ${userUuid} from ${startDate} to ${endDate}`
    );

    try {
      // Construct the endpoint with query parameters
      const endpoint = `/api/v1/app/email-report-generator/user?startDate=${encodeURIComponent(
        startDate
      )}&endDate=${encodeURIComponent(endDate)}`;

      const response = await APIService.get(endpoint);

      console.log(
        `[EmailReportGeneratorAPI] Reports generated successfully:`,
        response
      );
      return response;
    } catch (error) {
      console.error(
        `[EmailReportGeneratorAPI] Error generating reports:`,
        error
      );
      throw new Error(`Failed to generate email reports: ${error.message}`);
    }
  },

  /**
   * Generate email reports for all users within a time range
   * @param {string} startDate - Start date in ISO date-time format
   * @param {string} endDate - End date in ISO date-time format
   * @returns {Promise} - Promise that resolves with the API response
   */
  generateReportsForAllUsers: async (startDate, endDate) => {
    console.log(
      `[EmailReportGeneratorAPI] Generating reports for all users from ${startDate} to ${endDate}`
    );

    try {
      const endpoint = `/api/v1/app/email-report-generator/all?startDate=${encodeURIComponent(
        startDate
      )}&endDate=${encodeURIComponent(endDate)}`;

      const response = await APIService.get(endpoint);

      console.log(
        `[EmailReportGeneratorAPI] Reports for all users generated successfully:`,
        response
      );
      return response;
    } catch (error) {
      console.error(
        `[EmailReportGeneratorAPI] Error generating reports for all users:`,
        error
      );
      throw new Error(
        `Failed to generate email reports for all users: ${error.message}`
      );
    }
  },

  /**
   * Generate email reports for a user with simplified date format (last N days)
   * @param {string} userUuid - The user UUID
   * @param {number} days - Number of days back from today (default: 7)
   * @returns {Promise} - Promise that resolves with the API response
   */
  generateReportsForUserSimple: async (userUuid, days = 7) => {
    console.log(
      `[EmailReportGeneratorAPI] Generating reports for user ${userUuid} for last ${days} days`
    );

    try {
      const endpoint = `/api/v1/app/email-report-generator/user/simple?days=${days}`;

      const response = await APIService.get(endpoint);

      console.log(
        `[EmailReportGeneratorAPI] Simple reports generated successfully:`,
        response
      );
      return response;
    } catch (error) {
      console.error(
        `[EmailReportGeneratorAPI] Error generating simple reports:`,
        error
      );
      throw new Error(
        `Failed to generate simple email reports: ${error.message}`
      );
    }
  },

  /**
   * Synchronize email reports with SendGrid data
   * @param {string} startDate - Start date for synchronization
   * @param {string} endDate - End date for synchronization
   * @returns {Promise} - Promise that resolves with synchronization result
   */
  synchronizeEmailReports: async (startDate, endDate) => {
    console.log(
      `[EmailReportGeneratorAPI] Synchronizing email reports from ${startDate} to ${endDate}`
    );

    try {
      const endpoint = `/api/v1/app/email-report-generator/sync?startDate=${encodeURIComponent(
        startDate
      )}&endDate=${encodeURIComponent(endDate)}`;

      const response = await APIService.post(endpoint);

      console.log(
        `[EmailReportGeneratorAPI] Synchronization completed successfully:`,
        response
      );
      return response;
    } catch (error) {
      console.error(
        `[EmailReportGeneratorAPI] Error synchronizing reports:`,
        error
      );
      throw new Error(`Failed to synchronize email reports: ${error.message}`);
    }
  },

  /**
   * Health check for the email report generator service
   * @returns {Promise} - Promise that resolves with health status
   */
  healthCheck: async () => {
    console.log(`[EmailReportGeneratorAPI] Performing health check`);

    try {
      const endpoint = `/api/v1/app/email-report-generator/health`;

      const response = await APIService.get(endpoint);

      console.log(
        `[EmailReportGeneratorAPI] Health check completed:`,
        response
      );
      return response;
    } catch (error) {
      console.error(`[EmailReportGeneratorAPI] Health check failed:`, error);
      throw new Error(`Health check failed: ${error.message}`);
    }
  },
};

export default EmailReportGeneratorAPI;
